import { graphql } from "@generated/client";
import {
    type CreateFrameworkProjectInput,
    type FrameworkPatch,
    type GenerateCdpWorkplanInput,
    type GenerateEvsWorkplanInput,
} from "@generated/client/graphql";
import { AccessHeadersHolder } from "../AccessHeadersHolder";
import { graphqlClientKoyeb } from "../clients";

const GetFrameworksQuery = graphql(`
    query GetFrameworks {
        frameworks {
            id
            name
            description
            cmsId
            stats {
                reqOk
                reqTotal
            }
            activation
            order
            imageUrl
            released
            type
        }
    }
`);

const GetFrameworkQuestionnairesQuery = graphql(`
    query GetFrameworkQuestionnaires {
        frameworkQuestionnaires {
            id
            name
            cmsId
            stats: frameworkQuestionnaireDataPageStats {
                total
                answered
                aiSuggestionSuccess
                validated
                notAnswered
            }
        }
    }
`);

const GetFrameworkQuery = graphql(`
    query GetFramework($id: String!) {
        framework(cmsId: $id) {
            id
            name
            description
            groups {
                name
                subgroups {
                    name
                    sections {
                        name
                        description
                        requirements {
                            id
                            ownerId
                            cmsId
                            name
                            description
                            proofStat {
                                total
                                ok
                            }
                            status
                            order
                        }
                    }
                }
            }
        }
    }
`);

const GetFrameworkDocumentsQuery = graphql(`
    query getFrameworkDocuments($id: String!) {
        frameworkDocuments(id: $id) {
            id
            name
            description
            type
            frameworkSections(frameworkId: $id) {
                tagName
                name
            }
            lastVersion {
                finalFile {
                    signedUrl
                    extension
                }
            }
        }
    }
`);

const GetFrameworkCategoryQuery = graphql(`
    query GetFrameworkCategory($cmsGroupId: String!) {
        frameworkCategory(cmsGroupId: $cmsGroupId) {
            cmsGroupId
            name
            description
            articleLink
            demoLink
            officialWebsite
            imageUrl
            guide
            faq {
                question
                answer
            }
            projects {
                id
                name
                status
                totalQuestions
                validatedQuestions
                updatedAt
                creator {
                    firstName
                    lastName
                    profilePictureUrl
                }
                assignees {
                    firstName
                    lastName
                    profilePictureUrl
                }
            }
        }
    }
`);

const CreateFrameworkProjectMutation = graphql(`
    mutation CreateFrameworkProject($input: CreateFrameworkProjectInput!) {
        createFrameworkProject(input: $input) {
            data {
                id
            }
            status
        }
    }
`);

const UpdateFrameworkMutation = graphql(`
    mutation UpdateFramework($id: String!, $patch: FrameworkPatch!) {
        updateFramework(id: $id, patch: $patch) {
            id
        }
    }
`);

const InitializeFrameworkMutation = graphql(`
    mutation InitializeFramework($id: String!) {
        initializeFramework(id: $id) {
            id
        }
    }
`);

const GetReleasedCmsFrameworksQuery = graphql(`
    query GetReleasedCmsFrameworks {
        releasedCmsFrameworks {
            id
            name
            tags {
                id
                name
            }
            locale {
                availableLocales
            }
            type
            questionsCount
        }
    }
`);

const GetReleasedFrameworkCategoriesQuery = graphql(`
    query GetReleasedFrameworkCategories {
        releasedFrameworkCategories {
            cmsGroupId
            name
            locales
            versions
        }
    }
`);

const GetAvailableFrameworkCategoriesQuery = graphql(`
    query GetAvailableFrameworkCategories {
        availableFrameworkCategories {
            cmsGroupId
            name
            description
            activated
            released
            imageUrl
            officialWebsite
            lastUpdatedProjectId
        }
    }
`);

const GetFrameworkQuestionTagsQuery = graphql(`
    query frameworkTags($cmsGroupId: String!, $year: Float!, $locale: String!) {
        frameworkTags(cmsGroupId: $cmsGroupId, year: $year, locale: $locale) {
            id
            name
        }
    }
`);

const GetRecentFrameworkProjectsQuery = graphql(`
    query recentFrameworkProjects {
        recentFrameworkProjects {
            id
            name
            status
            totalQuestions
            validatedQuestions
            updatedAt
            creator {
                firstName
                lastName
                profilePictureUrl
            }
            assignees {
                firstName
                lastName
                profilePictureUrl
            }
            frameworkCategoryName
            frameworkCategoryCmsGroupId
        }
    }
`);

const GenerateCdpWorkplanMutation = graphql(`
    mutation GenerateCdpWorkplan($input: GenerateCdpWorkplanInput!) {
        generateCdpWorkplan(input: $input) {
            status
            data {
                signedUrl
            }
        }
    }
`);

const GenerateEvsWorkplanMutation = graphql(`
    mutation GenerateEvsWorkplan($input: GenerateEvsWorkplanInput!) {
        generateEvsWorkplan(input: $input) {
            status
            data {
                signedUrl
            }
        }
    }
`);

export class GraphQLFrameworkRepository {
    get = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetFrameworkQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getFrameworkQuestionTags = async (
        cmsGroupId: string,
        year: number,
        locale: string,
    ) => {
        return graphqlClientKoyeb.request(
            GetFrameworkQuestionTagsQuery,
            { cmsGroupId, year, locale },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getFrameworkCategory = async (cmsGroupId: string) => {
        return graphqlClientKoyeb.request(
            GetFrameworkCategoryQuery,
            { cmsGroupId },
            await AccessHeadersHolder.getHeaders(),
        );
    };
    getFrameworkDocuments = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetFrameworkDocumentsQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };
    getMany = async () => {
        return graphqlClientKoyeb.request(
            GetFrameworksQuery,
            undefined,
            await AccessHeadersHolder.getHeaders(),
        );
    };
    getManyFrameworkQuestionnaires = async () => {
        return graphqlClientKoyeb.request(
            GetFrameworkQuestionnairesQuery,
            undefined,
            await AccessHeadersHolder.getHeaders(),
        );
    };
    getManyReleasedFromCms = async () => {
        return graphqlClientKoyeb.request(
            GetReleasedCmsFrameworksQuery,
            undefined,
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getReleasedFrameworkCategories = async () => {
        return graphqlClientKoyeb.request(
            GetReleasedFrameworkCategoriesQuery,
            undefined,
            await AccessHeadersHolder.getHeaders(),
        );
    };

    update = async (id: string, patch: FrameworkPatch) => {
        return graphqlClientKoyeb.request(
            UpdateFrameworkMutation,
            { id, patch },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    initialize = async (id: string) => {
        return graphqlClientKoyeb.request(
            InitializeFrameworkMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getAvailableFrameworkCategories = async () => {
        return graphqlClientKoyeb.request(
            GetAvailableFrameworkCategoriesQuery,
            undefined,
            await AccessHeadersHolder.getHeaders(),
        );
    };

    createFrameworkProject = async (input: CreateFrameworkProjectInput) => {
        return graphqlClientKoyeb.request(
            CreateFrameworkProjectMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getRecentFrameworkProjects = async () => {
        return graphqlClientKoyeb.request(
            GetRecentFrameworkProjectsQuery,
            undefined,
            await AccessHeadersHolder.getHeaders(),
        );
    };

    generateCdpWorkplan = async (input: GenerateCdpWorkplanInput) => {
        return graphqlClientKoyeb.request(
            GenerateCdpWorkplanMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    generateEvsWorkplan = async (input: GenerateEvsWorkplanInput) => {
        return graphqlClientKoyeb.request(
            GenerateEvsWorkplanMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };
}
